import { CENTS_PER_KM_CAP_KM, CENTS_PER_KM_RATES, calculateCentsPerKm } from "@stb/jurisdiction-au"
import { useId, useState } from "react"

import type { Locale } from "@/core/i18n/locale"
import { useLocale } from "@/core/i18n/locale-context"
import { Input } from "@/ui/input"
import { Label } from "@/ui/label"

import { CENTS_PER_KM_COPY_ZH } from "../../content/zh/cents-per-km-copy"
import { displayYear } from "../home/home-content"
import { formatAud } from "./format-aud"

export { formatAud }

const capLabel = CENTS_PER_KM_CAP_KM.toLocaleString("en-AU")
const newestYear = CENTS_PER_KM_RATES.at(-1)?.financialYear ?? ""

/** Words of the calculator. Numbers are filled in by the component from the shared constants and the model. */
export interface CentsPerKmCopy {
  heading: string
  kmLabel: string
  /** "You can claim up to 5,000 km per car with this method." */
  hint: (cap: string) => string
  yearLabel: string
  /** Option text: "2026–27 (1 Jul 2026 – 30 Jun 2027)". */
  yearOption: (year: string, start: number) => string
  deduction: string
  /** "3,200 km × 91 c/km" */
  working: (km: string, cents: number) => string
  empty: string
  invalid: string
  overCap: (cap: string) => string
  note: string
}

const EN: CentsPerKmCopy = {
  heading: "Cents per km estimate",
  kmLabel: "Business kilometres this income year",
  hint: (cap) => `You can claim up to ${cap} km per car with this method.`,
  yearLabel: "Income year",
  yearOption: (year, start) => `${year} (1 Jul ${start} – 30 Jun ${start + 1})`,
  deduction: "Estimated deduction",
  working: (km, cents) => `${km} km × ${cents} c/km`,
  empty: "Enter your business kilometres to see an estimate.",
  invalid: "Use a whole number of kilometres, such as 3,200.",
  overCap: (cap) =>
    `This method counts at most ${cap} km per car. If you drive more for business, the logbook method may give a larger deduction.`,
  note: "Estimate only. Not tax advice. The rate covers fuel, registration, insurance, repairs and decline in value.",
}

const COPY: Record<Locale, CentsPerKmCopy> = { en: EN, zh: CENTS_PER_KM_COPY_ZH }

type Outcome =
  | { kind: "empty" }
  | { kind: "invalid" }
  | { kind: "over-cap" }
  | { kind: "ok"; km: number; cents: number; amount: string }

function evaluate(text: string, financialYear: string): Outcome {
  const digits = text.trim().replaceAll(",", "")
  if (digits === "") return { kind: "empty" }
  if (!/^\d+$/.test(digits)) return { kind: "invalid" }
  const km = Number(digits)
  const result = calculateCentsPerKm(financialYear, km)
  if (!result.ok) return { kind: "over-cap" }
  return { kind: "ok", km, cents: result.rate.cents, amount: formatAud(result.deduction) }
}

const selectClass =
  "border-input dark:bg-input/30 focus-visible:border-ring focus-visible:ring-ring/50 h-11 w-full rounded-md border bg-transparent px-3 text-base outline-none focus-visible:ring-[3px]"

/** The only interactive part of the rate guide: km x the year's rate, whole cents, Estimate only. */
export function CentsPerKmCalculator() {
  const id = useId()
  const copy = COPY[useLocale()]
  const [kmText, setKmText] = useState("3,200")
  const [financialYear, setFinancialYear] = useState(newestYear)
  const outcome = evaluate(kmText, financialYear)

  return (
    <div className="bg-card border-border grid gap-6 rounded-xl border p-5 md:grid-cols-2 md:p-6">
      <div className="grid content-start gap-3">
        <b className="text-lg">{copy.heading}</b>
        <div className="grid gap-1.5">
          <Label htmlFor={`${id}-km`}>{copy.kmLabel}</Label>
          <Input
            id={`${id}-km`}
            inputMode="numeric"
            autoComplete="off"
            className="h-11"
            value={kmText}
            onChange={(e) => setKmText(e.target.value)}
            aria-invalid={outcome.kind === "invalid" || outcome.kind === "over-cap"}
            aria-describedby={`${id}-hint ${id}-result`}
          />
          <p id={`${id}-hint`} className="text-muted-foreground text-sm">
            {copy.hint(capLabel)}
          </p>
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor={`${id}-fy`}>{copy.yearLabel}</Label>
          <select
            id={`${id}-fy`}
            className={selectClass}
            value={financialYear}
            onChange={(e) => setFinancialYear(e.target.value)}
          >
            {CENTS_PER_KM_RATES.toReversed().map((r) => (
              <option key={r.financialYear} value={r.financialYear}>
                {copy.yearOption(displayYear(r.financialYear), Number(r.financialYear.slice(0, 4)))}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div
        id={`${id}-result`}
        data-testid="calc-result"
        aria-live="polite"
        className="bg-muted/60 grid content-start gap-1 rounded-lg p-4"
      >
        {outcome.kind === "ok" ? (
          <>
            <span className="text-muted-foreground text-xs">{copy.deduction}</span>
            <span className="text-3xl font-bold tabular-nums">{outcome.amount}</span>
            <span className="text-muted-foreground text-sm">
              {copy.working(outcome.km.toLocaleString("en-AU"), outcome.cents)}
            </span>
          </>
        ) : (
          <p className="font-medium">
            {outcome.kind === "empty" && copy.empty}
            {outcome.kind === "invalid" && copy.invalid}
            {outcome.kind === "over-cap" && copy.overCap(capLabel)}
          </p>
        )}
        <p className="text-muted-foreground mt-2 text-xs">{copy.note}</p>
      </div>
    </div>
  )
}
